import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Trophy, Medal, Target, Gift, Settings, BookOpen, Sparkles } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { GamificationOverview } from '@/components/gamification/GamificationOverview';
import { BadgeConfigPanel } from '@/components/gamification/BadgeConfigPanel';
import { MilestoneBuilder } from '@/components/gamification/MilestoneBuilder';
import { RewardsCatalog } from '@/components/gamification/RewardsCatalog';
import { GamificationSettings } from '@/components/gamification/GamificationSettings';
import { GamificationTutorial } from '@/components/gamification/GamificationTutorial';

export function GamificationDashboard() {
  const { user } = useAuth();
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string>('');

  // Fetch company info — use user.company_id (tenant-scoped via tenant_memberships)
  useEffect(() => {
    async function fetchCompanyInfo() {
      if (!user?.company_id) return;

      const { data: company } = await supabase
        .from('companies')
        .select('name')
        .eq('id', user.company_id)
        .maybeSingle();

      setCompanyId(user.company_id);
      setCompanyName(company?.name || 'Company');
    }

    fetchCompanyInfo();
  }, [user]);

  // Fetch gamification settings
  const { data: settings, refetch: refetchSettings } = useQuery({
    queryKey: ['gamification-settings', companyId],
    queryFn: async () => {
      if (!companyId) return null;
      
      const { data, error } = await supabase
        .from('company_gamification_settings')
        .select('*')
        .eq('company_id', companyId)
        .maybeSingle();
      
      if (error) {
        console.error('Error fetching settings:', error);
      }
      
      return data;
    },
    enabled: !!companyId,
  });

  if (!companyId) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-96">
          <CardContent className="p-6 text-center">
            <Trophy className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">You are not associated with a company.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-6 py-8 space-y-8">
        {/* Header */}
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-xl bg-primary/10">
            <Sparkles className="h-8 w-8 text-primary" />
          </div>
          <div>
            <h1 className="text-4xl font-bold text-foreground">
              {settings?.gamification_title || 'Gamification Center'}
            </h1>
            <p className="text-muted-foreground text-lg">
              {companyName} - Configure engagement and rewards for your team
            </p>
          </div>
        </div>

        {/* Main Tabs */}
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-6 lg:w-auto lg:inline-flex h-auto p-1 bg-muted/50">
            <TabsTrigger value="overview" className="gap-2 px-4 py-2.5">
              <Trophy className="h-4 w-4" />
              <span className="hidden sm:inline">Overview</span>
            </TabsTrigger>
            <TabsTrigger value="badges" className="gap-2 px-4 py-2.5">
              <Medal className="h-4 w-4" />
              <span className="hidden sm:inline">Badges</span>
            </TabsTrigger>
            <TabsTrigger value="milestones" className="gap-2 px-4 py-2.5">
              <Target className="h-4 w-4" />
              <span className="hidden sm:inline">Milestones</span>
            </TabsTrigger>
            <TabsTrigger value="rewards" className="gap-2 px-4 py-2.5">
              <Gift className="h-4 w-4" />
              <span className="hidden sm:inline">Rewards</span>
            </TabsTrigger>
            <TabsTrigger value="settings" className="gap-2 px-4 py-2.5">
              <Settings className="h-4 w-4" />
              <span className="hidden sm:inline">Settings</span>
            </TabsTrigger>
            <TabsTrigger value="tutorial" className="gap-2 px-4 py-2.5">
              <BookOpen className="h-4 w-4" />
              <span className="hidden sm:inline">How It Works</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6">
            <GamificationOverview companyId={companyId} settings={settings} />
          </TabsContent>

          <TabsContent value="badges" className="mt-6">
            <BadgeConfigPanel companyId={companyId} />
          </TabsContent>

          <TabsContent value="milestones" className="mt-6">
            <MilestoneBuilder companyId={companyId} />
          </TabsContent>

          <TabsContent value="rewards" className="mt-6">
            <RewardsCatalog companyId={companyId} />
          </TabsContent>

          <TabsContent value="settings" className="mt-6">
            <GamificationSettings 
              companyId={companyId} 
              settings={settings} 
              onSettingsUpdated={refetchSettings}
            />
          </TabsContent>

          <TabsContent value="tutorial" className="mt-6">
            <GamificationTutorial />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

export default GamificationDashboard;
